import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {DictionaryService, FieldProcessService} from '@twpub/core/services';
import {Column, ColumnConfiguration, Dictionary, FieldDataRow, Language} from '@twpub/core/models';
import {forkJoin, Observable} from 'rxjs';
import {NGXLogger} from 'ngx-logger';
import {MatCard, MatCardContent, MatCardHeader} from '@angular/material/card';

import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {FormsModule} from '@angular/forms';
import {MatOptgroup, MatOption, MatSelect, MatSelectTrigger} from '@angular/material/select';
import {MatButton, MatMiniFabButton} from '@angular/material/button';

interface ColumnRow extends Column {
  id: string;
  selectedRow?: FieldDataRow;
  selectedLang?: string;
  editingLabel?: boolean;
}

@Component({
  selector: 'pub-column-config-editor',
  templateUrl: './column-config-editor.component.html',
  styleUrls: ['./column-config-editor.component.scss'],
  standalone: true,
  imports: [MatCard, MatCardHeader, MatCardContent, CdkDropList, CdkDrag, MatIcon, MatTooltip, MatFormField, MatInput, FormsModule, MatLabel, MatSelect, MatOption, MatSelectTrigger, MatOptgroup, MatMiniFabButton, MatButton]
})
export class ColumnConfigEditorComponent implements OnInit, OnChanges {
  @Input() dictionaryIds: number[] = [];
  @Input() columnConfig?: ColumnConfiguration;
  @Output() columnConfigChange = new EventEmitter<ColumnConfiguration>();

  columnRows: ColumnRow[] = [];
  fieldRows: FieldDataRow[] = [];
  conceptFields: FieldDataRow[] = [];
  termFields: FieldDataRow[] = [];
  languages: Language[] = [];
  errorMessage: string = '';

  // Localized labels
  addColumnLabel = $localize`:@@columnConfigEditor.addColumn:Add Column`;
  saveLabel = $localize`:@@columnConfigEditor.saveColumn:Save`;
  cancelLabel = $localize`:@@columnConfigEditor.cancelColumn:Cancel`;
  fieldLabel = $localize`:@@columnConfigEditor.field:Field`;
  languageLabel = $localize`:@@columnConfigEditor.language:Language`;
  conceptLevelLabel = $localize`:@@columnConfigEditor.conceptLevel:Concept Level Fields`;
  termLevelLabel = $localize`:@@columnConfigEditor.termLevel:Term Level Fields`;
  removeLabel = $localize`:@@columnConfigEditor.remove:Remove`;
  sourceTermsLabel = $localize`:@@columnConfigEditor.sourceTerms:Source Terms`;
  sourceTermsDescription = $localize`:@@columnConfigEditor.sourceTermsDescription:Fixed column showing source language term names`;
  targetTermsLabel = $localize`:@@columnConfigEditor.targetTerms:Target Terms`;
  targetTermsDescription = $localize`:@@columnConfigEditor.targetTermsDescription:Fixed column(s) showing target language term names`;
  titleLabel = $localize`:@@columnConfigEditor.title:Result List Columns`;
  columnLabel = $localize`:@@columnConfigEditor.column:Column`;
  columnLabelTooltip = $localize`:@@columnConfigEditor.columnLabelTooltip:Column label`;
  columnLanguageTooltip = $localize`:@@columnConfigEditor.columnLanguageTooltip:Column language`;
  columnFieldTooltip = $localize`:@@columnConfigEditor.columnFieldTooltip:Column field`;
  noneConceptLevelLabel = $localize`:@@columnConfigEditor.noneConceptLevel:None (Concept Level)`;
  termNameLabel = $localize`:@@columnConfigEditor.termName:Term name`;
  dictsLabel = $localize`:@@columnConfigEditor.dicts:dicts`;
  duplicateColumnsError = $localize`:@@columnConfigEditor.duplicateColumnsError:There are duplicate columns. Please ensure all columns have unique field-language combinations.`;
  private dictionaryLabel = $localize`:@@columnConfigEditor.dictionary:Dictionary`;
  private sectionLabel = $localize`:@@columnConfigEditor.section:Section`;
  private domainLabel = $localize`:@@columnConfigEditor.domain:Domain`;
  protected termNameField: FieldDataRow = {
    label: $localize`:@@columnConfigEditor.termNameField:Term Name`,
    level: 'Term',
    dictionaries: [],
    fieldKey: {jsonField: 'TERM', label: '', fieldLevel: 'Term', fieldOid: 'TERM'},
    searchable: false
  };

  constructor(private logger: NGXLogger, private dictionaryService: DictionaryService, private fieldProcessService: FieldProcessService,
              @Optional() private dialogRef?: MatDialogRef<ColumnConfigEditorComponent>, @Optional() @Inject(MAT_DIALOG_DATA) public data?: any
  ) {
    if (this.data) {
      this.dictionaryIds = this.data.dictionaryIds || [];
      this.columnConfig = this.data.columnConfig;
    }
  }

  ngOnInit(): void {
    this.loadFieldsAndLanguages();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dictionaryIds && !changes.dictionaryIds.firstChange) {
      this.loadFieldsAndLanguages();
    }
    if (changes.columnConfig && changes.columnConfig.firstChange) {
      this.initializeRows();
    }
  }

  private loadFieldsAndLanguages(): void {
    if (!this.dictionaryIds?.length) {
      this.fieldRows = [];
      this.languages = [];
      return;
    }

    const dictObservables: Observable<Dictionary>[] = this.dictionaryIds.map(id =>
      this.dictionaryService.getDictionary(id)
    );

    forkJoin(dictObservables).subscribe(dictionaries => {
      const fieldMap = this.fieldProcessService.getFieldMap(dictionaries, () => false);
      this.fieldRows = Array.from(fieldMap.values());

      // Add special concept-level fields
      const specialConceptFields: FieldDataRow[] = [
        {
          label: this.dictionaryLabel,
          level: 'Concept',
          dictionaries: [],
          fieldKey: {jsonField: 'DICT', label: this.dictionaryLabel, fieldLevel: 'Concept', fieldOid: 'DICT'},
          searchable: false
        },
        {
          label: this.sectionLabel,
          level: 'Concept',
          dictionaries: [],
          fieldKey: {jsonField: 'SECT', label: this.sectionLabel, fieldLevel: 'Concept', fieldOid: 'SECT'},
          searchable: false
        },
        {
          label: this.domainLabel,
          level: 'Concept',
          dictionaries: [],
          fieldKey: {jsonField: 'DOM', label: this.domainLabel, fieldLevel: 'Concept', fieldOid: 'DOM'},
          searchable: false
        }
      ];

      this.conceptFields = [
        ...specialConceptFields,
        ...this.fieldRows.filter(r => r.level === 'Concept').sort((a, b) => a.label.localeCompare(b.label))
      ];
      this.termFields = this.fieldRows.filter(r => r.level === 'Term').sort((a, b) => a.label.localeCompare(b.label));

      this.initializeRows();
    });

    this.dictionaryService.getLanguages(this.dictionaryIds).subscribe(languages => {
      this.languages = languages;
    });
  }

  private initializeRows(): void {
    if (this.columnConfig?.columns?.length) {
      this.columnRows = this.columnConfig.columns.map((col) => {
        let selectedRow = this.conceptFields.find(r => r.fieldKey.jsonField === col.field) || this.termFields.find(r => r.fieldKey.jsonField === col.field);
        if (!selectedRow && col.field === 'TERM') {
          selectedRow = this.termNameField;
        }
        return {
          ...col,
          id: this.generateId(),
          selectedRow,
          selectedLang: col.lang === undefined ? 'none' : col.lang,
          editingLabel: false
        };
      });
    } else {
      this.columnRows = [];
    }
  }

  addColumn(): void {
    const newRow: ColumnRow = {
      id: this.generateId(),
      field: this.conceptFields.length > 0 ? this.conceptFields[0].fieldKey.jsonField : '',
      label: this.conceptFields.length > 0 ? this.conceptFields[0].label : `Column ${this.columnRows.length + 1}`,
      lang: undefined,
      selectedLang: 'none',
      selectedRow: this.conceptFields.length > 0 ? this.conceptFields[0] : undefined,
      editingLabel: false
    };
    this.columnRows.push(newRow);
  }

  removeColumn(index: number): void {
    this.columnRows.splice(index, 1);
  }

  saveChanges(): void {
    // Check for duplicate fields considering language
    const error = this.validateConfig();
    this.errorMessage = error || '';
    if (error) {
      return;
    }
    this.columnConfigChange.emit(this.getConfig());
    if (this.dialogRef) {
      this.dialogRef.close(this.getConfig());
    }
  }

  private validateConfig = () => {
    const fieldLangKeys = this.columnRows.map(row => `${row.field}-${row.lang || 'concept'}`);
    const uniqueKeys = new Set(fieldLangKeys);
    if (fieldLangKeys.length !== uniqueKeys.size) {
      return this.duplicateColumnsError;
    }
    return null;
  }

  cancelChanges(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  onFieldChange(row: ColumnRow): void {
    const selRow = row.selectedRow;
    if (selRow) {
      row.field = selRow.fieldKey.jsonField
      row.dicts = selRow.dictionaries.length > 1 ? selRow.dictionaries.map(d => d.id) : undefined;
      if (row.field === 'TERM') {
        row.label = this.findLanguageName(row.lang || '');
      } else {
        row.label = selRow.label;
      }
      if (this.isConceptField(selRow)) {
        row.lang = undefined;
        row.selectedLang = 'none';
      }
    } else {
      row.field = '';
    }
  }

  onLanguageChange(row: ColumnRow): void {
    row.lang = row.selectedLang === 'none' ? undefined : row.selectedLang;
    if (!row.lang) {
      row.field = this.conceptFields.length > 0 ? this.conceptFields[0].fieldKey.jsonField : '';
      row.selectedRow = this.conceptFields.length > 0 ? this.conceptFields[0] : undefined;
    } else {
      row.field = 'TERM';
      row.selectedRow = this.termNameField;
    }
    this.onFieldChange(row); // Update label
  }

  isConceptField(row: FieldDataRow): boolean {
    return row.level === 'Concept';
  }

  private getConfig(): ColumnConfiguration {
    return {
      limitCols: this.columnConfig?.limitCols,
      columns: this.columnRows.map(row => ({
        field: row.field,
        label: row.label,
        lang: row.lang,
        dicts: row.dicts
      }))
    };
  }

  private generateId(): string {
    return `col-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  trackByRowId(index: number, row: ColumnRow): string {
    return row.id;
  }

  findLanguageName(code: String): string {
    const res: Language[] = this.languages.filter(l => l.code === code);
    return (res?.length) ? res[0].name : '';
  }

  drop(event: CdkDragDrop<ColumnRow[]>): void {
    moveItemInArray(this.columnRows, event.previousIndex, event.currentIndex);
  }

  startEditLabel(row: ColumnRow): void {
    row.editingLabel = true;
  }

  stopEditLabel(row: ColumnRow): void {
    row.editingLabel = false;
  }

  isMultipleDicts = (row?: FieldDataRow): boolean => !!row?.dictionaries && row.dictionaries.length > 1;
}
